import { useState } from "react";
import { SKILL_REPO_URL, DOCS_URL } from "./Nav.js";
import { DesktopDownload } from "./DesktopDownload.js";
import { InstallTicker } from "./InstallTicker.js";

// The single install command. One universal line — the skill installer
// detects the agent + platform itself, so the hero never needs OS or
// package-manager tabs. Kept in sync with FinalCTA.tsx.
const INSTALL_CMD = "curl -fsSL https://axonterminal.ai/install.sh | bash";

/**
 * Hero — two clear ways to run Axon, front and center:
 *   1. Headline + subhead   — the promise
 *   2. "For your AI agent"  — the copyable install command + Get the skill
 *   3. "Or run the desktop app" — one-click platform downloads (mac/win/linux)
 *   4. A quiet docs link
 *
 * Everything else (runtime logos, install counts, venue detail) lives
 * further down the page or in the docs.
 */
export function Hero() {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_CMD);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — the command is visible to select manually */
    }
  };

  return (
    <section className="ax-hero ax-grain">
      <div className="ax-hero-inner">
        <h1 className="ax-hero-h1">
          Install one skill.<br />
          Your agent can <span className="ax-accent">trade</span>.
        </h1>

        <p className="ax-hero-sub">
          A non-custodial skill any AI agent installs. Your keys never leave
          your machine.
        </p>

        {/* Social proof — live npm install count (honest: real registry
            data for @axon-trading/{cli,sdk,mcp}, ticking upward). */}
        <div className="ax-hero-proof">
          <InstallTicker variant="inline" />
          <span className="ax-hero-proof-sep" aria-hidden>·</span>
          <span>works in every agent</span>
        </div>

        {/* Path 1 — install the skill into any AI agent */}
        <div className="ax-hero-path">
          <div className="ax-hero-path-label">For your AI agent</div>
          <button
            type="button"
            className="ax-hero-cmd"
            onClick={copy}
            aria-label="Copy install command"
          >
            <span className="ax-hero-cmd-prompt">$</span>
            <code>{INSTALL_CMD}</code>
            <span className="ax-hero-cmd-copy">{copied ? "Copied" : "Copy"}</span>
          </button>
          <a href={SKILL_REPO_URL} target="_blank" rel="noreferrer" className="ax-btn ax-btn-primary ax-btn-lg">
            Get the skill
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        <div className="ax-hero-or" aria-hidden />

        {/* Path 2 — download the native desktop app */}
        <DesktopDownload />

        <a href={DOCS_URL} target="_blank" rel="noreferrer" className="ax-hero-cta-quiet ax-hero-docs">
          or read the docs →
        </a>
      </div>

      <style>{`
        .ax-hero {
          position: relative;
          overflow: hidden;
          padding: clamp(36px, 6vh, 88px) clamp(20px, 5vw, 96px);
          width: 100%;
          min-height: calc(100svh - 70px);
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .ax-hero-inner {
          position: relative;
          z-index: 2;
          max-width: 900px;
          width: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        /* Layered atmosphere — a soft accent aurora behind the headline +
           a faint grid that dissolves toward the edges (::before), and a
           vignette that pulls focus to center (::after). Both sit beneath
           .ax-hero-inner (z:2) and never intercept clicks. */
        .ax-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(58% 48% at 50% 36%, rgba(74,222,128,0.11) 0%, transparent 70%),
            linear-gradient(to right, var(--at-hairline) 1px, transparent 1px),
            linear-gradient(to bottom, var(--at-hairline) 1px, transparent 1px);
          background-size: 100% 100%, 64px 64px, 64px 64px;
          -webkit-mask-image: radial-gradient(78% 62% at 50% 40%, #000 0%, transparent 80%);
          mask-image: radial-gradient(78% 62% at 50% 40%, #000 0%, transparent 80%);
        }
        .ax-hero::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: radial-gradient(125% 85% at 50% 28%, transparent 56%, rgba(0,0,0,0.5) 100%);
        }

        /* Orchestrated entrance — each hero element rises + fades in
           sequence on load. Reduced-motion users get them instantly
           (globals.css collapses the duration). */
        .ax-hero-inner > * {
          animation: ax-fade-up 0.7s var(--at-ease-out) both;
        }
        .ax-hero-inner > *:nth-child(1) { animation-delay: 0.02s; }
        .ax-hero-inner > *:nth-child(2) { animation-delay: 0.10s; }
        .ax-hero-inner > *:nth-child(3) { animation-delay: 0.18s; }
        .ax-hero-inner > *:nth-child(4) { animation-delay: 0.26s; }
        .ax-hero-inner > *:nth-child(5) { animation-delay: 0.34s; }
        .ax-hero-inner > *:nth-child(6) { animation-delay: 0.42s; }

        .ax-hero-h1 {
          font-family: var(--at-font-display);
          font-size: clamp(34px, 5.4vw, 68px);
          font-weight: 700;
          letter-spacing: -0.035em;
          line-height: 1.03;
          margin: 0 0 clamp(16px, 2.4vh, 26px);
          /* Subtle vertical sheen on the display type — top bright, base
             slightly dimmed — for dimensional, premium headline weight. */
          background: linear-gradient(180deg, #ffffff 0%, #c4c8cf 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .ax-accent {
          color: var(--at-accent);
          -webkit-text-fill-color: var(--at-accent);
          font-family: var(--at-font-mono);
          font-weight: 700;
          letter-spacing: -0.03em;
          text-shadow: 0 0 26px rgba(74, 222, 128, 0.45);
        }
        .ax-hero-sub {
          font-size: clamp(16px, 1.4vw, 21px);
          line-height: 1.5;
          color: var(--at-fg-dim);
          margin: 0 0 clamp(16px, 2.2vh, 22px);
          max-width: 480px;
        }
        .ax-hero-proof {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 clamp(26px, 4vh, 38px);
          font-size: 13px;
          color: var(--at-muted);
        }
        .ax-hero-proof-sep { color: var(--at-hairline-strong); }

        /* Two stacked paths — "for your AI agent" (the install command +
           Get the skill) and "or run the desktop app" (platform buttons).
           Each path is a centered column; a hairline rule separates them. */
        .ax-hero-path {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          width: 100%;
        }
        .ax-hero-path-label {
          font-family: var(--at-font-mono);
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--at-muted);
        }
        .ax-hero-or {
          width: clamp(220px, 32vw, 360px);
          height: 1px;
          margin: clamp(26px, 4vh, 40px) 0;
          background: linear-gradient(to right, transparent, var(--at-hairline), transparent);
        }

        /* The single install command — the literal primary action.
           Click anywhere on the pill to copy. */
        .ax-hero-cmd {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          max-width: 100%;
          padding: 14px 16px 14px 20px;
          margin: 0;
          background: var(--at-surface);
          border: 1px solid var(--at-hairline);
          border-radius: 10px;
          cursor: pointer;
          font-family: var(--at-font-mono);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
          transition: border-color var(--at-dur-fast) var(--at-ease),
                      background var(--at-dur-fast) var(--at-ease),
                      box-shadow var(--at-dur-med) var(--at-ease);
        }
        .ax-hero-cmd:hover {
          border-color: rgba(74,222,128,0.45);
          background: rgba(74,222,128,0.04);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04),
                      0 0 0 1px rgba(74, 222, 128, 0.12),
                      0 10px 30px -14px rgba(74, 222, 128, 0.55);
        }
        .ax-hero-cmd-prompt {
          color: var(--at-accent);
          font-size: 14px;
          flex: none;
        }
        .ax-hero-cmd code {
          font-size: clamp(12px, 1.1vw, 15px);
          color: var(--at-fg);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ax-hero-cmd-copy {
          flex: none;
          margin-left: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          background: rgba(255,255,255,0.05);
          color: var(--at-fg-dim);
          font-size: 11px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .ax-hero-cmd:hover .ax-hero-cmd-copy { color: var(--at-fg); }

        .ax-btn-lg { padding: 12px 22px; font-size: 14px; }
        .ax-hero-cta-quiet {
          font-size: 14px;
          color: var(--at-fg-dim);
          transition: color var(--at-dur-fast) var(--at-ease);
        }
        .ax-hero-cta-quiet:hover { color: var(--at-fg); }
        .ax-hero-docs { margin-top: clamp(26px, 4vh, 40px); }

        @media (max-width: 600px) {
          .ax-hero-cmd code { font-size: 11px; }
        }
      `}</style>
    </section>
  );
}
